import { Alert, FormControlLabel, Radio, TextField } from "@mui/material";
import React, { useState } from "react";
import { TextInputBase } from "@truevault/aquilo";

type RadioWithTextFieldProps = {
  label: string | React.ReactNode;
  value: string | number | null;
  textValue?: string;
  textLabel: string;
  placeholder: string;
  error?: string;
  onRadioChanged?: (e: React.SyntheticEvent<Element, Event>, checked: boolean) => void;
  onTextChanged?: (value: string) => void;
  onTextCleared?: () => string;
  hideTextInput?: boolean;
  disabled?: boolean;
  loading?: boolean;
  selected?: boolean;
  useAquiloDesign?: boolean;
};

export const RadioWithTextField: React.FC<RadioWithTextFieldProps> = ({
  label,
  value,
  textValue,
  placeholder,
  textLabel,
  onRadioChanged,
  onTextChanged,
  onTextCleared,
  error = null,
  disabled = false,
  loading = false,
  selected = false,
  useAquiloDesign = false,
}) => {
  const [internalTextValue, setInternalTextValue] = useState(textValue ?? "");
  const [hasChanged, setHasChanged] = useState(false);

  let controlLabel = useAquiloDesign ? (
    <div className="flex items-center">
      {label}
      {!disabled && selected && (
        <div className="ml-[4px]">
          <TextInputBase
            field="text-field"
            onChange={(e) => {
              setInternalTextValue(e.target.value);
              setHasChanged(true);
              if (onTextChanged) {
                onTextChanged(e.target.value);
              }
            }}
            onBlur={() => {
              if (onTextChanged && hasChanged) {
                onTextChanged(internalTextValue);
                if (!internalTextValue && onTextCleared) {
                  setInternalTextValue(onTextCleared());
                }
              }
            }}
            onKeyDown={(e) => {
              if (onTextChanged && hasChanged && e.keyCode === 13) {
                onTextChanged(internalTextValue);
                if (!internalTextValue && onTextCleared) {
                  setInternalTextValue(onTextCleared());
                }
              }
            }}
            value={internalTextValue}
            label={textLabel}
            placeholder={placeholder}
            disabled={disabled || loading || !selected}
          />
        </div>
      )}
    </div>
  ) : (
    <div className="d-flex align-items-center">
      {label}
      {!disabled && selected && (
        <div className="ml-md flex-grow">
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            onChange={(e) => {
              setInternalTextValue(e.target.value);
              setHasChanged(true);
              if (onTextChanged) {
                onTextChanged(e.target.value);
              }
            }}
            onBlur={() => {
              if (onTextChanged && hasChanged) {
                onTextChanged(internalTextValue);
                if (!internalTextValue && onTextCleared) {
                  setInternalTextValue(onTextCleared());
                }
              }
            }}
            onKeyDown={(e) => {
              if (onTextChanged && hasChanged && e.keyCode === 13) {
                onTextChanged(internalTextValue);
                if (!internalTextValue && onTextCleared) {
                  setInternalTextValue(onTextCleared());
                }
              }
            }}
            value={internalTextValue}
            label={textLabel}
            placeholder={placeholder}
            disabled={disabled || loading || !selected}
          />

          {Boolean(error) && (
            <Alert className="mt-sm" color="error">
              {error}
            </Alert>
          )}
        </div>
      )}
    </div>
  );

  if (useAquiloDesign) {
    return controlLabel;
  }

  return (
    <FormControlLabel
      value={value}
      control={<Radio color="primary" />}
      onChange={onRadioChanged}
      className="radio-with-text-field"
      label={controlLabel}
      disabled={disabled || loading}
    />
  );
};
